<template>
  <div class="ks" data-test="kanban-settings">
    <SettingsCard title="Procedures">
      <FieldRow
        label="Proc codes"
        help="Optional. Leave it empty and this runs for every procedure the trigger fires on."
        :note="noProcCodes ? 'Runs for every procedure. Add codes to narrow it down.' : ''"
        note-tone="amber"
      >
        <WfPicker
          v-model="p.procCodes"
          :options="wfBuilder.procCodesList"
          :multiple="true"
          :searchable="true"
          :close-on-select="false"
          :show-labels="false"
          label="text"
          track-by="value"
          placeholder="Select proc codes…"
          :proc-codes="true"
        />
      </FieldRow>
    </SettingsCard>

    <SettingsCard title="Board" description="Configure how this workflow interacts with the selected Kanban board.">
      <FieldRow label="Kanban board">
        <WfPicker
          v-model="p.boardObj"
          :options="wfBuilder.kanbanBoards"
          :searchable="true"
          :show-labels="false"
          label="name"
          track-by="id"
          placeholder="Select board…"
          @input="onBoard"
        />
      </FieldRow>
      <FieldRow label="Column">
        <WfPicker
          v-model="p.columnObj"
          :options="wfBuilder.selectedBoardColumns"
          :searchable="true"
          :show-labels="false"
          label="name"
          track-by="id"
          placeholder="Select column…"
          @input="(val) => p.columnId = val ? val.id : ''"
        />
      </FieldRow>
    </SettingsCard>

    <SettingsCard title="Card">
      <FieldRow label="Card action">
        <WfPicker
          v-model="p.cardActionObj"
          :options="wfBuilder.kanbanCardActions"
          :searchable="false"
          :show-labels="false"
          label="text"
          track-by="value"
          placeholder="Select action…"
          @input="(val) => p.cardAction = val ? val.value : 'add'"
        />
      </FieldRow>

      <template v-if="p.cardAction !== 'move'">
        <FieldRow label="Card title" for-id="ks-card-title">
          <input
            id="ks-card-title"
            v-model="p.cardTitle"
            type="text"
            class="ks__input"
            placeholder="Enter card title…"
            data-test="kanban-card-title"
          />
        </FieldRow>
        <FieldRow label="Card description" for-id="ks-card-description">
          <textarea
            id="ks-card-description"
            v-model="p.cardDescription"
            class="ks__textarea"
            rows="3"
            placeholder="Enter card description…"
            data-test="kanban-card-description"
          ></textarea>
        </FieldRow>
        <FieldRow label="Card tags" help="Separate tags with commas." for-id="ks-card-tags">
          <input
            id="ks-card-tags"
            v-model="p.cardTags"
            type="text"
            class="ks__input"
            placeholder="tag1, tag2, tag3"
            data-test="kanban-card-tags"
          />
        </FieldRow>
      </template>

      <FieldRow v-if="p.cardAction === 'move'" label="Move to column">
        <WfPicker
          v-model="p.moveToColumnObj"
          :options="wfBuilder.selectedBoardColumns"
          :searchable="true"
          :show-labels="false"
          label="name"
          track-by="id"
          placeholder="Select destination column…"
          @input="(val) => p.moveToColumnId = val ? val.id : ''"
        />
      </FieldRow>
    </SettingsCard>
  </div>
</template>

<script>
/**
 * Kanban (design frame 1k): Procedures, Board, Card. The classic fields, bindings and side effects:
 * `procCodes`, `boardObj` (+ `onKanbanBoardSelect`, which also resets the column), `columnObj` /
 * `columnId`, `cardActionObj` / `cardAction`, `cardTitle` / `cardDescription` / `cardTags`, and
 * `moveToColumnObj` / `moveToColumnId` for the Move action.
 *
 * Empty proc codes mean "every procedure this trigger fires on", so Save asks first (the sheet's footer,
 * via `needsEmptyProcCodesConfirm`); this panel only shows the amber note.
 *
 * ⛔ No prop named patNum / siteID / loading / allSettings / isSuperAdmin / userID.
 */
import FieldRow from './FieldRow.vue';
import SettingsCard from './SettingsCard.vue';
import WfPicker from './WfPicker.vue';

export default {
  name: 'KanbanSettings',
  components: { FieldRow, SettingsCard, WfPicker },
  inject: ['wfBuilder'],

  computed: {
    p() {
      return (this.wfBuilder.selectedNodeData || {}).properties || {};
    },
    // The classic `proc-codes-empty-hint` condition.
    noProcCodes() {
      return !this.p.procCodes || this.p.procCodes.length === 0;
    },
  },

  methods: {
    onBoard(val) {
      this.wfBuilder.onKanbanBoardSelect(val);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../workflow-tokens';

.ks {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ks__input { @include wf-field(42px); }

.ks__textarea {
  @include wf-textarea;
  min-height: 84px;
}
</style>
